// ─────────────────────────────────────────────────────────────────────────────
// Aviso de cuánto espacio le queda al mensaje de un reconocimiento, compartido por las cuatro
// superficies donde se escribe: el formulario clásico, el post-it interactivo, la nominación de
// campaña y la edición posterior al envío.
//
// El criterio es preventivo y no punitivo: la caja de texto está topada en `MENSAJE_MAX_LENGTH`, así
// que el usuario nunca puede pasarse; lo que hace falta es que vea venir el límite. Nada se muestra
// mientras sobra espacio, aparece el conteo de restantes en la franja final y, al topar, un texto
// que dice qué hacer ("resume alguna parte") en vez de un error por algo que ya no puede evitar.
//
// También traduce el rechazo del backend cuando de todas formas llega. Las reglas de
// `CreateRecognitionCommandValidator` declaran un código estable (bug UAT #113) y aquí se mapea a la
// clave i18n correspondiente: sin esto, la pantalla mostraba el `title` genérico del 400 —«Error de
// validación en los datos enviados», en español y sin decir qué campo— para cinco causas distintas,
// dos de ellas ajenas al formulario (sesión sin `oid`, tenant sin resolver).
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import {
  MENSAJE_MAX_LENGTH,
  debeAvisarLimite,
  mensajeRestantes,
  getRecognitionErrorCode,
  getRecognitionErrorMessage,
} from '@/api/recognition';

interface MessageLimitHintProps {
  /** Largo del mensaje REAL que se enviará (en el post-it incluye la frase armada, no solo la caja). */
  readonly length: number;
  /** Tope aplicable; se parametriza solo para pruebas y para una pantalla con un tope propio. */
  readonly max?: number;
  /**
   * `true` en el post-it interactivo: aclara que la frase armada con el banco de palabras también
   * ocupa espacio. Sin esa nota, el conteo parece no cuadrar con lo que el usuario ve tecleado.
   */
  readonly hasBuilderSentence?: boolean;
}

/**
 * Aviso de límite del mensaje. No renderiza nada hasta entrar en la franja final
 * (`MENSAJE_WARN_REMAINING` caracteres antes del tope).
 */
export function MessageLimitHint({
  length,
  max = MENSAJE_MAX_LENGTH,
  hasBuilderSentence = false,
}: MessageLimitHintProps) {
  const { t } = useTranslation('recognition');
  if (!debeAvisarLimite(length, max)) return null;

  const remaining = mensajeRestantes(length, max);
  const isAtLimit = remaining === 0;

  return (
    <p className={`rp-note rp-chars-note ${isAtLimit ? 'rp-chars-note--full' : ''}`} role="status">
      <i className={`fas ${isAtLimit ? 'fa-circle-info' : 'fa-pen-to-square'}`} />
      {' '}
      {isAtLimit
        ? t('panel.charsLimitReachedHint', { max })
        : t('panel.charsAvailableHint', { remaining, max })}
      {hasBuilderSentence && <span className="rp-chars-note-extra">{t('panel.charsLimitSentenceNote')}</span>}
    </p>
  );
}

/**
 * Clave i18n por código de rechazo del alta de reconocimiento. Los declara
 * `CreateRecognitionCommandValidator` con `WithErrorCode`; un código nuevo que este front todavía no
 * mapee cae al texto del backend en vez de pintar una clave cruda.
 *
 * Emisor y tenant comparten mensaje a propósito: para quien envía, ambos son «la sesión no se pudo
 * identificar» y no hay nada que corregir en el formulario. La distinción (token sin `oid` frente a
 * token sin `tid`) sigue viviendo en el log del backend, que es donde sirve.
 */
const RECOGNITION_ERROR_CODE_KEYS: Readonly<Record<string, string>> = {
  'recognition.messageTooLong': 'panel.errMessageTooLong',
  'recognition.recipientRequired': 'panel.errRecipientRequired',
  'recognition.fundamentalRequired': 'panel.errFundamentalRequired',
  'recognition.senderUnknown': 'panel.errSessionUnknown',
  'recognition.tenantUnknown': 'panel.errSessionUnknown',
};

/**
 * Mensaje para un rechazo al enviar un reconocimiento: la traducción del código si el backend lo
 * mandó y lo conocemos, y si no el texto del backend (que viene en español) como último recurso.
 * Mismo patrón que `campaignErrorMessage` en `AdminCampaigns.tsx`.
 */
export function recognitionSendErrorMessage(
  error: unknown,
  translate: (key: string, options?: Record<string, string | number>) => string,
  fallback: string,
): string {
  const coded = getRecognitionErrorCode(error);
  const key = coded ? RECOGNITION_ERROR_CODE_KEYS[coded.code] : undefined;
  if (key) return translate(key, { ...coded?.params, max: MENSAJE_MAX_LENGTH });
  return getRecognitionErrorMessage(error, fallback);
}
